import React, { useCallback, useState } from 'react';
import { useSuccessCriteriaStats } from '../state/hooks';
import { useClassInsights } from './ClassInsightsContext';
import { statsBody } from './useFilters';
import { catalogClasses } from './catalogSelectors';
import { ALL_OPTION } from '../../utils/urlParams';
import { PanelHeader } from './PanelHeader';
import { PanelIconButton } from './PanelIconButton';
import { ExportFormatDropdown } from './ExportFormatDropdownNew';
import { SuccessCriteriaLegend } from './SuccessCriteriaLegend';
import { SECTIONS } from './export/fileNaming';
import {
  SUCCESS_CRITERIA_DEFAULT_SORT,
  SUCCESS_CRITERIA_SORT_OPTIONS,
  sortSuccessCriteriaObjectives,
  successCriteriaSortOrder,
} from './utils';
import { ExpandIcon } from './Icons';
import { SuccessCriteriaModal } from './SuccessCriteriaModal';
import { ToolEmptyState } from './EmptyState';
import DropdownMenu from '../DropdownMenu';
import SmallLoader from '../Loader/SmallLoader';
import DownLoad from '../../static/img/Download.svg';
import successCriteriaIcon from '../../static/img/markUnSelected.svg';
import QuestionTooltip from '../../components2/QuestionTooltip';

const MIN_SEGMENT_LABEL_PERCENT = 8;
const shouldShowSegmentLabel = (value) => Number(value || 0) > MIN_SEGMENT_LABEL_PERCENT;
const isShowAll = (ids) => Array.isArray(ids) && ids.length === 1 && ids[0] === ALL_OPTION;

export function SuccessCriteriaPanel({
  mode = 'small',
  onExpand,
  onCollapse,
  open = true,
  onExited,
  exportMode = false,
}) {
  const {
    filters,
    debouncedFilters,
    setFilters,
    catalog,
    successCriteriaSortBy,
    setSuccessCriteriaSortBy,
  } = useClassInsights();

  // The small tile and the expanded modal are two separate instances of this
  // component, so sort state held here cannot survive the expand -- the modal
  // would mount fresh on the default. ClassInsightsPage owns it above both and
  // passes it through context. The local state is only a fallback for a provider
  // that does not supply it; hooks must run unconditionally either way.
  const [localSortBy, setLocalSortBy] = useState(SUCCESS_CRITERIA_DEFAULT_SORT);
  const isSortShared =
    successCriteriaSortBy !== undefined && typeof setSuccessCriteriaSortBy === 'function';
  const sortBy = isSortShared ? successCriteriaSortBy : localSortBy;
  const setSortBy = isSortShared ? setSuccessCriteriaSortBy : setLocalSortBy;

  const sortOrder = successCriteriaSortOrder(sortBy);

  // JED-1066: empty classIds means "all classes", not "no classes". Gate the
  // query on whether the catalog has any class at all -- mirroring
  // KpiSummaryCards -- so selecting "All Classes" still loads this report
  // instead of leaving the panel blank while the KPIs show data.
  const hasClassScope = catalogClasses(catalog).length > 0;
  const { data, isFetching: isTileLoading } = useSuccessCriteriaStats(
    { ...tileBody(debouncedFilters, debouncedFilters.successCriteriaIds), sortBy: 'satisfaction', sortOrder },
    hasClassScope
  );

  const list = data?.options || [];
  const stats = data?.stats || [];

  const getSelectedIds = () => {
    if (list.length === 0) return [];

    if (isShowAll(filters.successCriteriaIds)) return [];

    if (filters.successCriteriaIds && filters.successCriteriaIds.length > 0) {
      const validIds = filters.successCriteriaIds.filter((id) =>
        list.some((item) => String(item.id) === String(id))
      ).map(String);
      if (validIds.length > 0) return validIds;
    }

    return [String(list[0].id)];
  };
  const selectedIds = getSelectedIds();

  // Clean up stale URL IDs when scope changes. Skip when URL is empty (don't seed)
  // and preserve the "All" sentinel.
  const currentFilterIds = (filters.successCriteriaIds || []).map(String).join(',');
  const validFilterIds = selectedIds.join(',');
  if (
    list.length > 0 &&
    currentFilterIds &&
    currentFilterIds !== validFilterIds &&
    !isShowAll(filters.successCriteriaIds)
  ) {
    setFilters({ successCriteriaIds: selectedIds });
  }

  const onChange = useCallback((ids) => {
    if (!ids || ids.length === 0) {
      setFilters({ successCriteriaIds: [ALL_OPTION] });
      return;
    }
    const validIds = (ids || []).filter(id =>
      list.some(item => String(item.id) === String(id))
    );
    setFilters({ successCriteriaIds: validIds });
  }, [setFilters, list]);
  const dropdownUiProps = {
    triggerFontSize: '16px',
    triggerPadding: '10px 16px',
  };

  const sortOptions = SUCCESS_CRITERIA_SORT_OPTIONS;

  const allItems = Array.isArray(stats) ? stats : [];

  const items = selectedIds.length === 0
    ? allItems
    : allItems.filter((item) => selectedIds.some(id => String(item.id) === String(id)));

  const csv = items.flatMap((criteria) =>
    sortSuccessCriteriaObjectives(criteria.objectives || [], sortOrder).map((objective) => ({
      Criteria: criteria.title || 'Untitled Success Criteria',
      Objective: objective.title || '',
      Satisfied: formatOneDecimal(objective.satisfiedPercentage),
      PartiallySatisfied: formatOneDecimal(objective.partiallySatisfiedPercentage),
      NotSatisfied: formatOneDecimal(objective.notSatisfiedPercentage),
    }))
  );

  if (mode === 'expanded') {
    return (
      <SuccessCriteriaModal
        open={open}
        onClose={onCollapse}
        onExited={onExited}
        list={list}
        selectedIds={selectedIds}
        onChange={onChange}
        stats={stats}
        isLoading={isTileLoading}
        exportMode={exportMode}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />
    );
  }

  return (
    <section className="ci-panel" data-section="success-criteria">
      <PanelHeader title="Success Criteria">
        <DropdownMenu
          multiselect
          options={list}
          selectedValues={selectedIds}
          onChange={onChange}
          placeholder="Select success criteria"
          getOptionLabel={(opt) => opt.title}
          getOptionValue={(opt) => String(opt.id)}
          alwaysWhite
          showAllOption
          showAllWhenAllSelected
          allLabel="All"
          leadingIcon={successCriteriaIcon}
          compactDisplay
          triggerWidth="clamp(120px, 40vw, 350px)"
          {...dropdownUiProps}
        />
        <ExportFormatDropdown
          section={SECTIONS.SUCCESS_CRITERIA}
          data={items}
          csvData={csv}
          trigger={
            <PanelIconButton aria-label="Download">
              <img src={DownLoad} className="ci-download-icon" alt="" />
            </PanelIconButton>
          }
        />
        <PanelIconButton
          type="button"
          onClick={onExpand}
          aria-label="Expand"
          className="ci-icon-button-expand"
        >
          <ExpandIcon />
        </PanelIconButton>
      </PanelHeader>
      <div className="ci-sort-controls">
        {!isTileLoading && items.length > 0 && (
          <>
            <span className="ci-sort-label">Sort by:</span>
            <DropdownMenu
              multiselect
              singleSelect
              options={sortOptions}
              selectedValues={[sortBy]}
              onChange={(values) => setSortBy(values[0] || sortOptions[0])}
              placeholder="Sort"
              getOptionLabel={(opt) => opt}
              getOptionValue={(opt) => opt}
              alwaysWhite
              compactDisplay
              showCheckboxes={false}
              showAllOption={false}
              triggerFontSize="14px"
              triggerPadding="8px 12px"
              triggerWidth="auto"
              triggerMinWidth="280px"
            />
          </>
        )}
      </div>
      <SuccessCriteriaLegend variant="panel" />
      <div className="ci-success-content">
        {isTileLoading ? (
          <div className="ci-panel-loading">
            <SmallLoader />
          </div>
        ) : items.length === 0 ? (
          <ToolEmptyState tool="successCriteria" />
        ) : (
          items.map((criteria) => (
            <div key={criteria.id} style={{ marginBottom: items.length > 1 ? '24px' : '0' }}>
              <div className="ci-success-subject">
                {criteria.title || 'Untitled Success Criteria'}
              </div>
              {sortSuccessCriteriaObjectives(criteria.objectives || [], sortOrder).map((objective, index) => (
                <div key={`${objective.title}-${index}`} className="ci-success-row-wrap">
                  <div className="ci-success-row-title">{objective.title}</div>
                  <QuestionTooltip
                    text={`Satisfied: ${formatOneDecimal(objective.satisfiedPercentage)} | Partially Satisfied: ${formatOneDecimal(objective.partiallySatisfiedPercentage)} | Not Satisfied: ${formatOneDecimal(objective.notSatisfiedPercentage)}`}
                  >
                    <div className="ci-success-bar">
                      <div
                        className="ci-success-segment ci-satisfied"
                        style={{ width: `${Number(objective.satisfiedPercentage || 0)}%` }}
                      >
                        {shouldShowSegmentLabel(objective.satisfiedPercentage) ? formatOneDecimal(objective.satisfiedPercentage) : ''}
                      </div>
                      <div
                        className="ci-success-segment ci-partially"
                        style={{ width: `${Number(objective.partiallySatisfiedPercentage || 0)}%` }}
                      >
                        {shouldShowSegmentLabel(objective.partiallySatisfiedPercentage) ? formatOneDecimal(objective.partiallySatisfiedPercentage) : ''}
                      </div>
                      <div
                        className="ci-success-segment ci-not-satisfied"
                        style={{ width: `${Number(objective.notSatisfiedPercentage || 0)}%` }}
                      >
                        {shouldShowSegmentLabel(objective.notSatisfiedPercentage) ? formatOneDecimal(objective.notSatisfiedPercentage) : ''}
                      </div>
                    </div>
                  </QuestionTooltip>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

    </section>
  );
}

const formatOneDecimal = (value) => `${Number(value || 0).toFixed(1)}%`;

const tileBody = (filters, ids) => ({
  ...statsBody(filters),
  markingToolIds: ids && ids.length > 0 && !isShowAll(ids) ? ids : null,
});
